import PropTypes from 'prop-types';

import useRequest from 'core/hooks/useRequest';
import { bebop as bebopApi } from 'core/api/definitions/bebop';

import Loader from 'core/components/Loader';
import withTheme from 'core/hocs/withTheme';
import withoutSSR from 'core/components/withoutSSR';
import { Redirect } from 'core/libs/router';
import EmptyWrapper from 'core/components/EmptyWrapper';
import Comments from 'core/components/Comments';

import themePropTypes from 'core/utils/prop-types/theme';
import { denormalizeData } from 'core/utils/api';
import modelPropTypes, { topicAttributes } from 'core/utils/prop-types/model';
import resolveRelationships from 'core/utils/relationships';

import { getDataAggregation, getGameCompareFromLS } from '../utils';
import { TARGET_TYPE, GAME_CMPARE } from '../constants';

import List from './List';
import Concidences from '../Concidences';

import styles from './index.styl';

const relationships = resolveRelationships(['content'], {});


function LeaderBoard(props) {
  const {
    isWinnerPage,
    content,
    theme: {
      controls: {
        gameCompare: {
          showComments,
          leaderBoard: {
            table: {
              indent: Indent,
            },
          },
        },
      },
    },
  } = props;
  const gameCompare = relationships(content).content?.widgets?.[0] || {};
  const {
    id: targetId,
    attributes: {
      variants: variantsFromWidget = [],
    } = {},
  } = gameCompare;
  const variantsIds = variantsFromWidget.map(variant => variant.attributes.variant_id + 1).join(',');

  const results = useRequest({ queries: [{
    queryKey: ['bebopApi', 'getReactions', { // reactions
      target_uuid: targetId,
      target_type: TARGET_TYPE,
      gc_variant_ids: variantsIds,
    }],
    queryFn: ({ queryKey: [, method, params] }) => bebopApi[method](params).catch(() => ({})),
    select: ({ data }) => data,
    enabled: !!variantsIds,
  }, {
    queryKey: ['bebopApi', 'getTopics', {
      limit: 5,
      include: 'content',
      fields: 'link,headline,listHeadline,published_at',
      topic_type: 'game_compare',
      excluded_ids: content.id,
    }],
    queryFn: ({ queryKey: [, method, params] }) => bebopApi[method](params),
    select: denormalizeData,
    enabled: !!content?.id,
  }] });

  if (results.some(({ isLoading }) => isLoading)) return <Loader />;

  const [{ data: reactions }, { data: topics }] = results;

  if (!reactions) return null;

  const {
    link,
  } = content.attributes;

  const gameCompareFromLs = getGameCompareFromLS(gameCompare.id, GAME_CMPARE);
  const isGameNotCompleted = gameCompareFromLs.variants.length > 1
    || gameCompareFromLs.variants.length === 0;

  if (isGameNotCompleted) return <Redirect to={link} />;

  const winnerId = gameCompareFromLs.variants[0].id;
  const { variants } = gameCompare.attributes;
  const { gc_common: gcCommon = [] } = reactions[gameCompare.id] || {};

  let winnerIndex = 0;
  let startIndex = 0;
  const reactionsResult = getDataAggregation(gcCommon, variants);
  let leaders = reactionsResult;
  let active = reactionsResult.findIndex(variant => variant.id === winnerId);

  const isWinner = winnerId
    && (leaders.filter(variant => variant.id === winnerId)).length > 0
    && isWinnerPage;

  if (isWinner) {
    winnerIndex = leaders.findIndex(variant => variant.id === winnerId);
    if (winnerIndex === 0) {
      leaders = reactionsResult.slice(0, 3);
    } else if (winnerIndex === reactionsResult.length - 1) {
      leaders = reactionsResult.slice(-3);
      active = 2;
      startIndex = winnerIndex - 2;
    } else {
      leaders = [reactionsResult[winnerIndex - 1], reactionsResult[winnerIndex], reactionsResult[winnerIndex + 1]];
      active = 1;
      startIndex = winnerIndex - 1;
    }
  }

  return (
    <EmptyWrapper>
      {isWinner &&
        <Concidences winnerIndex={winnerIndex} reactionsResult={reactionsResult} />
      }
      {leaders.length > 0 &&
        <Indent>
          <List
            variants={leaders}
            active={active}
            isWinnerPage={isWinner}
            topics={topics}
            link={link}
            startIndex={startIndex}
          />
        </Indent>
      }
      {showComments && (
        <div className={styles.comments}>
          <Comments xid={content.id} />
        </div>
      )}
    </EmptyWrapper>
  );
}

LeaderBoard.propTypes = {
  content: modelPropTypes(topicAttributes),
  widget: PropTypes.object,
  isWinnerPage: PropTypes.bool,
  theme: themePropTypes(`
    {
      controls: {
        gameCompare: {
          showComments,
        },
      },
    },
  `),
};

export default withoutSSR(withTheme(LeaderBoard));
